import React, { useEffect, useRef, useState } from "react";
import { useApp } from "@/context/AppContext";
import { resolveMediaUrl } from "@/components/FileUpload";
import { NEUTRAL_POSTER, onPosterError } from "@/lib/neutralPoster";
import { isHlsSrc, posterFor, attachHls } from "@/lib/videoSource";
import { imageVariant } from "@/lib/mediaUrl";
import { attachAdVideoTracking } from "@/lib/adVideoTrack";
import { createMusicFader } from "@/lib/audioFade";
import { useVideoResume, resumeEnabled, getResume, resumeFinished, RESUME_MIN_SECONDS } from "@/lib/videoProgress";
import { Maximize2, Radio, Loader2, RotateCcw, RotateCw } from "lucide-react";
import { fxStyle } from "@/lib/mediaFx";

// Double appui (façon YouTube) : moitié gauche = −10 s, moitié droite = +10 s.
const DOUBLE_TAP_MS = 320;
const SEEK_STEP_S = 10;
import { SoundControl } from "@/components/SoundControl";
import { enterVideoFullscreen } from "@/lib/videoFullscreen";
import { PlaybackSpeedControl } from "@/components/PlaybackSpeedControl";

// Reel-style teaser rules for long feed videos (muted loop of the opening only).
const FEED_PREVIEW_SECONDS = 6;
const LONG_VIDEO_MIN = 60;

// Budget de lecteurs du Fil BRANCHÉS à leur source (hls.js / src) : Android n'a que ~4-8 décodeurs matériels et
// la WebView gèle quelques secondes quand ils sont épuisés (vidéos qui refusent de démarrer). Un lecteur ne se
// branche qu'à l'approche de l'écran (~¾ d'écran de marge), se débranche en s'éloignant, et au plus
// FEED_ATTACH_MAX lecteurs sont branchés en même temps (une place libérée réveille le suivant).
export const FEED_ATTACH_MAX = 4;
const FEED_NEAR_MARGIN = "75% 0px 75% 0px";
const FEED_SLOT_EVENT = "yalola:feed-video-slot";
const attachedFeed = new Set();
export const attachedFeedCount = () => attachedFeed.size;

export const useFeedAttachSlot = (ref) => {
  const [near, setNear] = useState(false);
  const [slot, setSlot] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") { setNear(true); return undefined; }
    const io = new IntersectionObserver(([e]) => setNear(!!e?.isIntersecting), { rootMargin: FEED_NEAR_MARGIN, threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, [ref]);
  useEffect(() => {
    if (!near) return undefined;
    const token = {};
    let held = false;
    const claim = () => {
      if (held || attachedFeed.size >= FEED_ATTACH_MAX) return held;
      attachedFeed.add(token); held = true; setSlot(true);
      return true;
    };
    if (!claim()) window.addEventListener(FEED_SLOT_EVENT, claim);
    return () => {
      window.removeEventListener(FEED_SLOT_EVENT, claim);
      setSlot(false);
      if (held) { attachedFeed.delete(token); window.dispatchEvent(new Event(FEED_SLOT_EVENT)); }
    };
  }, [near]);
  return slot;
};


export const fmtDur = (s) => {
  if (s == null || isNaN(s)) return null;
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60);
  return h ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}` : `${m}:${String(sec).padStart(2, "0")}`;
};

// Facebook-style inline feed video: autoplays muted when in view, pauses when
// out of view. Sound is a GLOBAL toggle applied to every feed video at once.
// Cloudflare Stream replays AND Bunny VOD play through hls.js.
const FeedPlayer = ({ src, poster, className = "", testId, onExpand, isReplay = false, duration = null, initialProcessing = false, hdProcessing = false, compact = false, musicUrl = null, musicTitle = null, keepMediaSound = false, musicVolume = 1, musicStart = 0, hasAudio = null, flush = false, adId = null, resumeKey = null, resumeAt = 0, width = 0, height = 0, fx = null }) => {
  const { videoMuted, setVideoMuted, t, user, config } = useApp();
  const ref = useRef(null);
  const audioRef = useRef(null);
  // Dimensions connues (post.video_width/height) → la bonne hauteur est réservée AVANT le 1er frame (zéro saut).
  const [ratio, setRatio] = useState(() => (width > 0 && height > 0 ? width / height : null));
  const [processing, setProcessing] = useState(!!initialProcessing);
  const [ready, setReady] = useState(false);
  const [realDur, setRealDur] = useState(null);
  const [curT, setCurT] = useState(0);
  const [musicPlaying, setMusicPlaying] = useState(false);
  // Resume where the member stopped (account-wide): only a DELIBERATE watch (sound on)
  // resumes and records — the muted teaser loop never touches the saved position.
  const resumeOn = !!resumeKey && !!user && resumeEnabled(config);
  const [savedT, setSavedT] = useState(() => Math.max(Number(resumeAt) || 0, getResume(resumeKey)));
  useEffect(() => { setSavedT(Math.max(Number(resumeAt) || 0, getResume(resumeKey))); }, [resumeKey, resumeAt]);
  useVideoResume(ref, { key: resumeKey, enabled: resumeOn && !videoMuted, initial: Number(resumeAt) || 0, onSave: (tt) => setSavedT(tt) });
  const hls = isHlsSrc(src);
  const hasMusic = !!musicUrl;
  // Background music replaces the video's own audio unless the author chose to
  // keep it. The video element is then always muted (music carries the sound).
  const videoElMuted = hasMusic && !keepMediaSound ? true : videoMuted;
  // Adopt the real aspect ratio (clamped) so portrait / non-16:9 videos keep their
  // size — live replays included (a phone live is portrait); 16:9 until measured.
  const boxRatio = ratio ? Math.min(Math.max(ratio, 0.6), 1.9) : 16 / 9;
  // explicit poster -> HLS-derived still (Cloudflare + Bunny) -> neutral branded
  // still. Never leave the <video> poster empty → Android WebView would otherwise
  // paint its ugly default grey play placeholder while the frame decodes.
  // Affiche envoyée (/api/files/…) → variante allégée ≤ largeur de la carte (les affiches HLS Bunny sont inchangées).
  const realPoster = posterFor(src, poster ? imageVariant(poster, 640) : poster);
  const posterUrl = realPoster || NEUTRAL_POSTER;
  const markReady = () => { setReady(true); setProcessing(false); };
  const attached = useFeedAttachSlot(ref);
  const visibleRef = useRef(false);

  // Branché seulement à l'approche de l'écran (budget global) : HLS via hls.js, MP4 par `src` ; en s'éloignant
  // la source est retirée → décodeur et tampon libérés, l'affiche réapparaît.
  useEffect(() => {
    const el = ref.current;
    if (!el || !attached) return undefined;
    const cleanup = hls
      ? attachHls(el, src, { autoPlay: () => visibleRef.current, onReady: () => markReady(), onProcessing: (b) => setProcessing(b) })
      : (() => { el.src = resolveMediaUrl(src); if (visibleRef.current) el.play().catch(() => {}); return () => { try { el.removeAttribute("src"); el.load(); } catch { /* noop */ } }; })();
    return () => { cleanup(); setReady(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, hls, attached]);

  // Autoplay muted when in view; pause when out of view.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => { visibleRef.current = !!entry.isIntersecting; if (entry.isIntersecting) el.play().catch(() => {}); else el.pause(); },
      { threshold: 0.6 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => { if (ref.current) ref.current.muted = videoElMuted; }, [videoElMuted]);

  // Sponsored video analytics: starts + 25/50/75/100% milestones + watch seconds.
  useEffect(() => {
    const el = ref.current;
    if (!el || !adId) return;
    return attachAdVideoTracking(el, adId);
  }, [adId]);

  // Story-style background music: an <audio> synced to the video's play/pause,
  // muted-by-default like the feed (tap "Sans son" to hear it), with fade in/out.
  useEffect(() => {
    const v = ref.current;
    const a = audioRef.current;
    if (!v || !a || !hasMusic) return undefined;
    a.src = resolveMediaUrl(musicUrl);
    a.loop = !musicStart;
    a.muted = videoMuted;
    a.volume = 0;
    const startAt = musicStart || 0;
    const fader = createMusicFader(a, {});
    fader.setTarget(musicVolume);
    fader.setStart(startAt);
    fader.setGate(0);
    let pauseT = null;
    const onPlay = () => { if (pauseT) { clearTimeout(pauseT); pauseT = null; } try { a.currentTime = startAt; } catch { /* noop */ } a.play().then(() => { setMusicPlaying(true); fader.fadeIn(); }).catch(() => {}); };
    const onPause = () => { fader.fadeOut(); if (pauseT) clearTimeout(pauseT); pauseT = setTimeout(() => { try { a.pause(); } catch { /* noop */ } }, 320); setMusicPlaying(false); };
    const onEnded = () => { if (startAt) { try { a.currentTime = startAt; a.play().catch(() => {}); } catch { /* noop */ } } };
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    a.addEventListener("ended", onEnded);
    if (!v.paused) onPlay();
    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      a.removeEventListener("ended", onEnded);
      if (pauseT) clearTimeout(pauseT);
      fader.destroy();
      try { a.pause(); } catch { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [musicUrl, hasMusic, musicVolume, musicStart]);

  useEffect(() => { if (audioRef.current) audioRef.current.muted = videoMuted; }, [videoMuted]);

  // Duration badge: total length until playback starts, then the REMAINING time counts down
  // (like the theater player). The muted 6-second teaser of long videos keeps the total —
  // its loop would otherwise make the counter jump back every few seconds.
  const totalDur = (realDur && isFinite(realDur) && realDur > 0) ? realDur : (Number(duration) || null);
  const teaser = videoMuted && totalDur > LONG_VIDEO_MIN;
  const durStr = isReplay ? fmtDur((totalDur && curT > 0 && !teaser) ? Math.max(0, totalDur - curT) : totalDur) : null;
  return (
    <div className={`relative w-full overflow-hidden bg-black ${flush ? "" : "mt-3 rounded-xl border"} ${className}`} style={{ borderColor: "var(--yalola-border)", aspectRatio: boxRatio, maxHeight: compact ? "var(--feed-video-max-h-compact)" : "var(--feed-video-max-h)" }} data-testid={testId ? `${testId}-box` : undefined}>
      {posterUrl && (
        <div className="absolute inset-0 z-0 pointer-events-none"
          style={{ backgroundImage: `url(${posterUrl})`, backgroundSize: "cover", backgroundPosition: "center", filter: "blur(28px)", transform: "scale(1.18)" }} aria-hidden />
      )}
      {isReplay && (
        <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide text-white pointer-events-none"
          style={{ background: "rgba(217,87,87,0.94)", backdropFilter: "blur(4px)" }} data-testid={testId ? `${testId}-replay-badge` : "replay-badge"}>
          <Radio size={11} /> Replay
        </div>
      )}
      {durStr && (
        <div className="absolute bottom-3 left-3 z-10 px-2 py-0.5 rounded text-[11px] font-semibold text-white pointer-events-none"
          style={{ background: "rgba(0,0,0,0.72)" }} data-testid={testId ? `${testId}-duration` : "replay-duration"} data-remaining={curT > 0 && !teaser ? "1" : "0"}>
          {durStr}
        </div>
      )}
      <video
        ref={ref}
        poster={posterUrl || undefined}
        muted={videoElMuted}
        loop
        playsInline
        preload="metadata"
        onLoadedData={() => markReady()}
        onPlaying={() => markReady()}
        onLoadedMetadata={(e) => {
          const v = e.target;
          if (v.videoWidth && v.videoHeight) setRatio(v.videoWidth / v.videoHeight);
          if (isFinite(v.duration) && v.duration > 0) setRealDur(v.duration);
        }}
        onDurationChange={(e) => {
          const v = e.target;
          if (isFinite(v.duration) && v.duration > 0) setRealDur(v.duration);
        }}
        onTimeUpdate={(e) => {
          const v = e.currentTarget;
          if (isReplay) setCurT(Math.floor(v.currentTime)); // same integer → no re-render
          // Reel-style teaser for LONG videos: while muted, loop only the first
          // seconds instead of streaming an hour in the feed. Turning the sound
          // on (deliberate watch) switches to full playback.
          if (!videoMuted) return;
          const d = isFinite(v.duration) && v.duration > 0 ? v.duration : (duration || 0);
          if (d > LONG_VIDEO_MIN && v.currentTime >= FEED_PREVIEW_SECONDS) {
            try { v.currentTime = 0; } catch { /* noop */ }
          }
        }}
        className={`relative z-[1] w-full h-full object-contain ${onExpand ? "cursor-pointer" : ""}`}
        style={fxStyle(fx)}
        onClick={() => {
          if (onExpand) { onExpand(); return; }
          const v = ref.current; if (v) (v.paused ? v.play() : v.pause());
        }}
        data-testid={testId}
      />
      {posterUrl && (
        <img src={posterUrl} alt="" loading="lazy" decoding="async" aria-hidden onError={onPosterError}
          className="absolute inset-0 z-[2] w-full h-full object-contain pointer-events-none transition-opacity duration-500"
          style={{ opacity: ready ? 0 : 1, ...(fxStyle(fx) || {}) }}
          data-testid={testId ? `${testId}-cover` : "video-cover"} />
      )}
      {attached && !ready && !processing && !realPoster && (
        <div className="absolute inset-0 z-[5] flex items-center justify-center pointer-events-none" style={{ background: "rgba(0,0,0,0.6)" }}
          data-testid={testId ? `${testId}-loading` : "video-loading"}>
          <Loader2 size={22} className="animate-spin" style={{ color: "rgba(255,255,255,0.6)" }} />
        </div>
      )}
      <SoundControl
        muted={videoMuted}
        onToggle={() => setVideoMuted(!videoMuted)}
        hasAudio={hasMusic ? true : hasAudio}
        variant="compact"
        className="absolute bottom-3 right-3 z-20"
        noAudioLabel={t("video.no_sound", "Sans son")}
        muteLabel={t("video.mute", "Couper le son")}
        hintLabel={t("video.tap_for_sound", "Appuyez pour le son")}
        testId={testId ? `${testId}-sound` : "video-sound"}
      />
      {(() => {
        // YouTube-style « watched so far » bar: the saved position over the video duration.
        const d = (realDur && realDur > 0) ? realDur : (Number(duration) || 0);
        const pct = resumeOn && d > 0 && savedT >= RESUME_MIN_SECONDS && !resumeFinished(savedT, d) ? Math.min(100, (savedT / d) * 100) : 0;
        if (!pct) return null;
        return (
          <div className="absolute left-0 right-0 bottom-0 z-[6] h-[3px] pointer-events-none" style={{ background: "rgba(255,255,255,0.25)" }}
            data-testid={testId ? `${testId}-resume-bar` : "video-resume-bar"} data-pct={Math.round(pct)}>
            <div className="h-full" style={{ width: `${pct}%`, background: "var(--yalola-primary)" }} />
          </div>
        );
      })()}
      {hasMusic && (
        <div
          className="absolute top-3 left-3 z-20 flex items-center gap-1.5 rounded-full bg-black/55 text-white px-2.5 py-1 text-xs font-semibold pointer-events-none"
          style={{ backdropFilter: "blur(4px)", textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}
          data-testid={testId ? `${testId}-music` : "post-video-music"}
        >
          <span className={`eq ${musicPlaying && !videoMuted ? "eq-on" : ""}`} aria-hidden><i /><i /><i /><i /></span>
          <span className="max-w-[150px] truncate">{musicTitle || t("post.music", "Musique")}</span>
        </div>
      )}
      {hasMusic && <audio ref={audioRef} className="hidden" data-testid={testId ? `${testId}-music-audio` : "post-video-music-audio"} />}
      {onExpand && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onExpand(); }}
          className="absolute top-3 right-3 z-20 h-9 w-9 rounded-full grid place-items-center text-white transition-transform hover:scale-110"
          style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
          aria-label={t("video.fullscreen", "Plein écran")} data-testid={`${testId}-expand`}>
          <Maximize2 size={15} />
        </button>
      )}
      {processing && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 text-white pointer-events-none px-4 text-center"
          style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(2px)" }}
          data-testid={testId ? `${testId}-processing` : "video-processing"}>
          <Loader2 size={26} className="animate-spin" />
          <span className="text-sm font-medium">{t("video.processing", "Vidéo en cours de traitement…")}</span>
        </div>
      )}
      {hdProcessing && !processing && (
        <div className="absolute top-2 left-2 z-20 flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold text-white pointer-events-none"
          style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
          data-testid={testId ? `${testId}-hd-processing` : "video-hd-processing"}>
          <Loader2 size={12} className="animate-spin" />
          {t("video.hd_processing", "Traitement HD…")}
        </div>
      )}
    </div>
  );
};

// <video controls> that fades in only once a frame is ready (hides the Android
// WebView buffering spinner). HLS-capable so it also plays Cloudflare / Bunny.
// `adaptRatio` sizes the box to the video's real aspect ratio (16:9 until measured,
// clamped, capped by `maxHeight`) — for inline use inside a scrolling post view.
// `autoPlay` starts WITH sound; if the browser blocks unmuted autoplay it falls back
// to muted playback (the native controls let the viewer turn the sound back on).
const ControlsPlayer = ({
  src,
  poster,
  className = "",
  videoClassName = "w-full h-full object-contain bg-black",
  testId = "playable-video",
  autoPlay = false,
  muted = false,
  loop = false,
  controls = true,
  adaptRatio = false,
  maxHeight = "var(--inline-video-max-h)",
  initialProcessing = false,
  children = null,
  resumeKey = null,
  resumeAt = 0,
  width = 0,
  height = 0,
  fullscreenButton = false,
  speedControl = false,
  fx = null,
}) => {
  const { t, user, config } = useApp();
  const ref = useRef(null);
  const autoTriedRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [err, setErr] = useState(false);
  const [ratio, setRatio] = useState(() => (width > 0 && height > 0 ? width / height : null));
  const [processing, setProcessing] = useState(!!initialProcessing);
  const hls = isHlsSrc(src);
  const posterUrl = posterFor(src, poster ? imageVariant(poster, 640) : poster) || NEUTRAL_POSTER;
  useVideoResume(ref, { key: resumeKey, enabled: !!resumeKey && !!user && resumeEnabled(config), initial: Number(resumeAt) || 0 });
  // Double appui (doigt / stylet) gauche/droite → −10 s / +10 s ; la barre de contrôles native en bas est
  // ignorée. À la souris, le double-clic garde son sens natif (plein écran, comme YouTube sur PC) —
  // Chrome le déclenche dans ses contrôles et il n'est pas annulable.
  const tapRef = useRef({ t: 0, x: 0 });
  const [seekFx, setSeekFx] = useState(null);
  const onTap = (e) => {
    const v = ref.current;
    if (!v || !controls || e.pointerType === "mouse" || (e.target instanceof Element && e.target.closest("button"))) return;
    const box = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - box.left, y = e.clientY - box.top;
    if (y > box.height - 64) { tapRef.current = { t: 0, x: 0 }; return; }
    const now = Date.now();
    const prev = tapRef.current;
    if (now - prev.t < DOUBLE_TAP_MS && Math.abs(prev.x - x) < 80) {
      const dir = x < box.width / 2 ? -1 : 1;
      const dur = Number.isFinite(v.duration) ? v.duration : Infinity;
      try { v.currentTime = Math.max(0, Math.min(dur, v.currentTime + dir * SEEK_STEP_S)); } catch { /* not seekable yet */ }
      setSeekFx({ dir, id: now });
      tapRef.current = { t: 0, x: 0 };
    } else {
      tapRef.current = { t: now, x };
    }
  };
  useEffect(() => {
    if (!seekFx) return undefined;
    const tm = setTimeout(() => setSeekFx(null), 650);
    return () => clearTimeout(tm);
  }, [seekFx]);

  useEffect(() => {
    const el = ref.current;
    if (!el || !hls) return;
    setReady(false); setErr(false);
    return attachHls(el, src, { autoPlay, onReady: () => { setReady(true); setProcessing(false); }, onProcessing: (b) => setProcessing(b), onAutoplayBlocked: () => {} });
  }, [src, hls, autoPlay]);

  // Plain mp4: the `autoPlay` attribute is silently ignored for unmuted media without a
  // user gesture → try once, then fall back to muted playback.
  const tryAutoPlay = () => {
    const el = ref.current;
    if (!el || hls || !autoPlay || autoTriedRef.current) return;
    autoTriedRef.current = true;
    const pr = el.play();
    if (pr && pr.catch) pr.catch((e) => { if (e?.name === "NotAllowedError") { el.muted = true; el.play().catch(() => {}); } });
  };

  const showPoster = (!ready || err) && !!posterUrl;
  const boxRatio = ratio ? Math.min(Math.max(ratio, 0.6), 1.9) : 16 / 9;
  return (
    <div className={`relative overflow-hidden bg-black ${className}`} style={adaptRatio ? { aspectRatio: boxRatio, maxHeight } : undefined} data-testid={testId ? `${testId}-box` : undefined}
      onPointerUp={onTap}>
      {adaptRatio && posterUrl && (
        <div className="absolute inset-0 z-0 pointer-events-none" aria-hidden
          style={{ backgroundImage: `url(${posterUrl})`, backgroundSize: "cover", backgroundPosition: "center", filter: "blur(28px)", transform: "scale(1.18)" }} />
      )}
      {showPoster && (
        <img src={posterUrl} alt="" aria-hidden loading="lazy" decoding="async" onError={onPosterError}
          className="absolute inset-0 z-[1] w-full h-full object-contain pointer-events-none" style={fxStyle(fx)}
          data-testid={testId ? `${testId}-poster` : "video-poster"} />
      )}
      <video
        ref={ref}
        src={hls ? undefined : resolveMediaUrl(src)}
        poster={posterUrl}
        controls={controls}
        autoPlay={hls ? false : autoPlay}
        muted={muted}
        loop={loop}
        playsInline
        preload="auto"
        onLoadedData={() => { setReady(true); tryAutoPlay(); }}
        onCanPlay={() => { setReady(true); tryAutoPlay(); }}
        onPlaying={() => { setReady(true); setProcessing(false); }}
        onLoadedMetadata={(e) => { const v = e.target; if (v.videoWidth && v.videoHeight) setRatio(v.videoWidth / v.videoHeight); }}
        onError={() => { if (!hls) setErr(true); }}
        style={{ opacity: ready && !err ? 1 : 0, transition: "opacity .35s ease", ...(fxStyle(fx) || {}) }}
        className={`relative z-[1] ${adaptRatio ? videoClassName.replace(/\bbg-black\b/, "") : videoClassName}`}
        data-testid={testId}
      />
      {!ready && !err && (
        <div className="absolute inset-0 z-[2] grid place-items-center pointer-events-none" style={{ background: posterUrl ? "rgba(0,0,0,0.25)" : "#111" }}>
          <Loader2 size={22} className="animate-spin" style={{ color: "rgba(255,255,255,0.7)" }} />
        </div>
      )}
      {err && (
        <div className="absolute inset-0 z-[2] grid place-items-center text-white/80 text-xs font-medium pointer-events-none" style={{ background: posterUrl ? "rgba(0,0,0,0.35)" : "#111" }}>
          {t("video.unavailable", "Vidéo indisponible")}
        </div>
      )}
      {processing && !err && (
        <div className="absolute inset-0 z-[3] flex flex-col items-center justify-center gap-2 text-white pointer-events-none px-4 text-center"
          style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(2px)" }}
          data-testid={testId ? `${testId}-processing` : "video-processing"}>
          <Loader2 size={26} className="animate-spin" />
          <span className="text-sm font-medium">{t("video.processing", "Vidéo en cours de traitement…")}</span>
        </div>
      )}
      {seekFx && (
        <div key={seekFx.id} className={`absolute inset-y-0 z-[4] w-1/2 grid place-items-center pointer-events-none yl-seek-fx ${seekFx.dir < 0 ? "left-0" : "right-0"}`}
          data-testid={testId ? `${testId}-seek-${seekFx.dir < 0 ? "back" : "fwd"}` : undefined}>
          <div className="flex flex-col items-center gap-1 text-white">
            <div className="h-14 w-14 rounded-full grid place-items-center" style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}>
              {seekFx.dir < 0 ? <RotateCcw size={26} /> : <RotateCw size={26} />}
            </div>
            <span className="text-xs font-bold" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.7)" }}>{seekFx.dir < 0 ? "−" : "+"}{SEEK_STEP_S} s</span>
          </div>
        </div>
      )}
      {(fullscreenButton || speedControl) && !err && (
        <div className="absolute top-3 right-3 z-[5] flex items-center gap-2">
          {speedControl && <PlaybackSpeedControl videoRef={ref} testId={testId || "video"} />}
          {fullscreenButton && (
            <button type="button" onClick={(e) => { e.stopPropagation(); enterVideoFullscreen(ref.current); }}
              className="h-9 w-9 rounded-full grid place-items-center text-white transition-transform hover:scale-110"
              style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
              aria-label={t("video.fullscreen", "Plein écran")} data-testid={`${testId}-fullscreen`}>
              <Maximize2 size={16} />
            </button>
          )}
        </div>
      )}
      {children}
    </div>
  );
};

// The ONE universal video player. `mode="feed"` (default) = autoplay-in-view
// inline feed/replay video; `mode="controls"` = <video controls> for
// theater / chat / archive / church detail. Both handle Cloudflare + Bunny HLS.
export const VideoPlayer = ({ mode = "feed", ...props }) =>
  mode === "controls" ? <ControlsPlayer {...props} /> : <FeedPlayer {...props} />;

export default VideoPlayer;
